// One SPOKEN name, one thing — the rule Alexa needs.
//
// ── What Alexa actually hears (2026-10-18) ─────────────────────────────────
//
// Alexa does NOT hear the app's names as they are. The skill (alxea-lambda,
// `buildFriendlyName` in src/device_types.js) builds every endpoint's spoken
// name itself:
//
//   switch panel / fan    one endpoint PER BUTTON  "<Room> <Button name>"
//   curtain, gate, lock,  one endpoint PER DEVICE  "<Room> <Device name>"
//   RGB, WWH, WWRGB
//
// with the LOCATION name standing in for the room on a Common device (RoomID 0),
// everything but letters, digits and spaces stripped, and "Bedroom" + "Bedroom
// Light" kept as "Bedroom Light". The `AlexaName` column is never read.
//
// So the rule is: no two SPOKEN names may repeat. A button "Light" in the Hall
// and another in the Bedroom are fine ("Hall Light", "Bedroom Light"); a device
// and its button may share a name; a switch panel's own name is not spoken at
// all, so it can never clash. The first version compared the raw names across
// the whole account and refused all of those. ⚠️ Keep `ONE_ENDPOINT_IDS` and
// `spokenName` in step with the skill — it is the definition, this is a copy.
//
// The skill also appends the output number when two spoken names still repeat,
// so a duplicate is not fatal — but "Hall Light 2" is not what anybody says.
//
// ⚠️ ABOUT HALF THE EXISTING ESTATE MAY ALREADY COLLIDE, and the customer did
// not do it (factory default names repeat on every panel in a room). So this
// rule may ONLY block a name being newly set. It must never fire on a name that
// was already there, or rooms become unsavable — see `changedNames` in the
// callers, and the note on `findClash`'s `selfKey`.

const KEY_BUTTON = 'b';
const KEY_DEVICE = 'd';

export const buttonKey = (uniqueNumber, output) => `${KEY_BUTTON}:${uniqueNumber}:${output}`;
export const deviceKeyOf = (uniqueNumber) => `${KEY_DEVICE}:${uniqueNumber}`;
const unoOfKey = (key) => String(key || '').split(':')[1] || '';
const isDeviceKey = (key) => String(key || '').startsWith(`${KEY_DEVICE}:`);

/**
 * Device types Alexa sees as ONE endpoint named after the DEVICE (the skill's
 * CURTAIN_DEVICE_IDS + LOCK_DEVICE_IDS + COLOR_KIND_BY_DEVICE_ID). Every other
 * type is one endpoint per button, named after the BUTTON.
 */
export const ONE_ENDPOINT_IDS = [13, 51, 37, 3, 24, 25, 90, 23];
export const isOneEndpoint = (deviceId) => ONE_ENDPOINT_IDS.includes(Number(deviceId));

/**
 * A name compared as Alexa hears it: case and spacing only.
 * (Callers also use it to tell "unchanged" from "renamed".)
 */
export const normaliseName = (s) => String(s ?? '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLowerCase();

// The skill's `clean`: only letters, digits and spaces survive.
const clean = (v) => String(v ?? '').replace(/[^A-Za-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

/** The skill's `buildFriendlyName`: "<place> <thing>", place = room, else location. */
export function spokenName(place, thing) {
  const p = clean(place);
  const t = clean(thing);
  if (!t) return '';
  if (p && t.toLowerCase().startsWith(`${p.toLowerCase()} `)) return t;
  return p ? `${p} ${t}` : t;
}

/**
 * Everything in the account that Alexa hears, indexed by the normalised SPOKEN
 * name. Pure — the caller supplies the lists.
 *
 * -> Map<normalised spoken, entry[]>, entry = { key, kind, name, spoken, device, room, uno }
 *    plus `index.where`: Map<uno, { place, room, oneEndpoint }> — what findClash
 *    needs to work out how a NEW name on that device would be spoken.
 */
export function buildNameIndex({
  outputs = [], devices = [], rooms = [], locations = [],
} = {}) {
  const roomName = new Map(
    rooms.map((r) => [String(r.RoomID ?? r.RoomId), String(r.RoomName ?? '').trim()]),
  );
  const locName = new Map(
    locations.map((l) => [String(l.LocationID ?? l.LocationId), String(l.LocationName ?? '').trim()]),
  );

  const where = new Map();
  devices.forEach((d) => {
    const rid = String(d.RoomID ?? d.RoomId ?? '');
    // RoomID 0 means the device belongs to the location, not a room.
    const room = (rid && rid !== '0') ? (roomName.get(rid) || '') : '';
    const loc = locName.get(String(d.LocationID ?? d.LocationId ?? '')) || '';
    where.set(String(d.UniqueNumber), {
      device: String(d.DeviceName ?? '').trim(),
      room,
      place: room || loc,
      oneEndpoint: isOneEndpoint(d.DeviceID),
    });
  });

  const index = new Map();
  index.where = where;
  const add = (spoken, entry) => {
    // ⚠️ NO PLACE, NO CHECK. A device whose room/location is not in this
    // account's lists (live: user 1 had six such "S1"s) is spoken by the skill
    // with a place we cannot see — indexing it bare would invent clashes.
    const w = where.get(entry.uno);
    if (!w || !w.place) return;
    const k = normaliseName(spoken);
    // An unnamed button is not exposed to Alexa — it can neither clash nor be clashed with.
    if (!k) return;
    if (!index.has(k)) index.set(k, []);
    index.get(k).push({ ...entry, spoken });
  };

  const firstOutputName = new Map();
  outputs.forEach((o) => {
    const uno = String(o.UniqueNumber);
    if (Number(o.Output) === 1) firstOutputName.set(uno, String(o.OutputName ?? '').trim());
  });

  devices.forEach((d) => {
    const uno = String(d.UniqueNumber);
    const w = where.get(uno);
    if (!w.oneEndpoint) return;   // a switch panel's own name is never spoken
    const name = String(d.DeviceName ?? '').trim() || firstOutputName.get(uno) || '';
    add(spokenName(w.place, name), {
      key: deviceKeyOf(uno), kind: 'device', name, uno, device: w.device, room: w.room,
    });
  });

  outputs.forEach((o) => {
    const uno = String(o.UniqueNumber);
    const w = where.get(uno);
    // A one-endpoint device is spoken by its DEVICE name; its buttons are not endpoints.
    if (w && w.oneEndpoint) return;
    const name = String(o.OutputName ?? '').trim();
    add(spokenName(w ? w.place : '', name), {
      key: buttonKey(uno, o.Output),
      kind: 'button',
      name,
      uno,
      output: o.Output,
      device: w ? w.device : '',
      room: w ? w.room : '',
    });
  });

  return index;
}

/**
 * How `name`, given to the thing behind `selfKey`, would be spoken — or '' when
 * Alexa would not hear it at all (a switch panel's own name, a button on a
 * one-endpoint device, or a device this account's lists do not hold).
 */
export function spokenFor(index, name, selfKey) {
  const w = index && index.where ? index.where.get(unoOfKey(selfKey)) : null;
  if (!w || !w.place) return '';
  if (isDeviceKey(selfKey) !== w.oneEndpoint) return '';
  return spokenName(w.place, name);
}

/**
 * Who else Alexa would hear by the same SPOKEN name, if `name` were given to
 * the thing behind `selfKey` — null when it is free, or not spoken at all.
 *
 * ⚠️ `selfKey` IS NOT OPTIONAL IN PRACTICE. Renaming a button to the name it
 * already has, or re-saving a dialog without touching the name, would otherwise
 * clash with the row being edited — the commonest possible action failing.
 *
 * -> the first conflicting entry, or null.
 */
export function findClash(index, name, selfKey) {
  if (!index) return null;
  const k = normaliseName(spokenFor(index, name, selfKey));
  if (!k) return null;
  const hits = index.get(k) || [];
  return hits.find((e) => e.key !== selfKey) || null;
}

/**
 * Where a clashing name already lives, for the message shown to the operator.
 *
 * ⚠️ IT MUST ALWAYS SAY SOMETHING FINDABLE, and the first version did not. Run
 * against a live account it produced a bare `a button “S2”` — because plenty of
 * real install rows carry an EMPTY DeviceName, and some sit in no room. A block
 * that names no location is a block the customer cannot act on: they are told
 * the name is taken and given nowhere to look. So the unique number stands in
 * when the name does not, and the button number comes along with it.
 */
export function describeClash(entry) {
  if (!entry) return '';
  if (entry.kind === 'device') {
    const where = entry.room ? ` in ${entry.room}` : '';
    return `the device “${entry.name}”${where}`;
  }
  const on = entry.device || (entry.uno ? `device ${entry.uno}` : '');
  const bits = [on, entry.room].filter(Boolean).join(' · ');
  const btn = entry.output != null ? `button ${entry.output}` : 'a button';
  return bits ? `${btn} “${entry.name}” (${bits})` : `${btn} “${entry.name}”`;
}

/**
 * The whole message, so every caller words it identically.
 *
 * Names the clash rather than just refusing: "already in use" leaves the
 * operator hunting through every room for the other one.
 */
export const clashMessage = (entry) => (entry
  ? `Alexa would hear “${entry.spoken || entry.name}” twice — it is already ${describeClash(entry)}. `
    + 'Alexa names (room + name) must not repeat.'
  : '');

/**
 * Every SPOKEN name that more than one thing answers to — the existing mess.
 *
 * -> [{ name, count, entries }] worst first, then alphabetical so two runs of
 *    the same report agree.
 */
export function duplicateGroups(index) {
  const out = [];
  (index || new Map()).forEach((entries, k) => {
    if (entries.length > 1) out.push({ name: entries[0].spoken || entries[0].name || k, count: entries.length, entries });
  });
  return out.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/**
 * Check a batch of renames — the desktop dialog saves several boxes at once.
 *
 * ⚠️ THE BATCH MUST BE CHECKED AGAINST ITSELF, not only against the server.
 * Typing "Light 1" into two boxes of the same dialog passes every per-row check
 * and then writes the very duplicate this exists to prevent.
 *
 * `edits` is [{ key, name }] — only the ones that actually CHANGED.
 * -> { key, name, clash } for the first offender, or null.
 */
export function findBatchClash(index, edits) {
  const seen = new Map();
  for (const e of (edits || [])) {
    const spoken = spokenFor(index, e.name, e.key);
    const k = normaliseName(spoken);
    if (!k) continue;
    if (seen.has(k)) {
      return {
        key: e.key,
        name: e.name,
        clash: {
          kind: 'button', name: seen.get(k).name, spoken, device: '', room: '',
        },
      };
    }
    seen.set(k, e);
    const clash = findClash(index, e.name, e.key);
    if (clash) return { key: e.key, name: e.name, clash };
  }
  return null;
}
